import React from 'react';
import { Activity, P } from '@fashionnovateam/nova-ui-toolkit';
import {
  PurchaseOrder,
  PurchaseOrderActivity,
  PurchaseOrderActivityAuthor,
  PurchaseOrderActivityAuthorType,
  PurchaseOrderActivityType,
  PurchaseOrderRevisionStatusMap,
} from '../../../Lib/Types/purchase-order';
import { formatDate } from '../../PoCard/utils';
import { PurchaseOrderRevisionRequestedActivity } from '../../PoCard/Revision/Activity';
import {
  PurchaseOrderRevisionRequestedActivityDetail,
  PurchaseOrderRevisionStatus,
} from '../../../Lib/Types/purchase-order-revision';

export const renderActivityDetail = (
  activity: PurchaseOrderActivity,
  purchaseOrder: PurchaseOrder,
): [React.ReactNode] | [React.ReactNode, React.ReactNode] => {
  switch (activity.type) {
    case PurchaseOrderActivityType.STATUS_UPDATED:
    case PurchaseOrderActivityType.VENDOR_ACCEPTED:
    case PurchaseOrderActivityType.CANCELLED:
    case PurchaseOrderActivityType.TOP_REJECTED:
    case PurchaseOrderActivityType.TOP_APPROVED:
    case PurchaseOrderActivityType.TOP_RECEIVED:
    case PurchaseOrderActivityType.VENDOR_REJECTED:
    case PurchaseOrderActivityType.DELIVERY_RECEIVED:
    case PurchaseOrderActivityType.VENDOR_TOP_SHIPPED:
    case PurchaseOrderActivityType.PAYMENT_PENDING:
    case PurchaseOrderActivityType.PRE_PROD_APPROVED:
    case PurchaseOrderActivityType.BULK_PRODUCTION_COMPLETED:
    case PurchaseOrderActivityType.BULK_PRODUCTION_STAGE_UPDATED:
    case PurchaseOrderActivityType.TOP_PARTIALLY_RECEIVED:
    case PurchaseOrderActivityType.TOP_PARTIALLY_REJECTED:
    case PurchaseOrderActivityType.TOP_PARTIALLY_APPROVED:
    case PurchaseOrderActivityType.TOP_NOT_REQUIRED:
    case PurchaseOrderActivityType.DELIVERY_CHECK_IN:
    case PurchaseOrderActivityType.RECEIVING_DELIVERY:
      return [
        <>
          updated the status from{' '}
          <span className="text-base-primary font-semibold">
            {activity.details?.sub_stage?.old_value || 'Undefined'}
          </span>{' '}
          to{' '}
          <span className="text-base-primary font-semibold">
            {activity.details?.sub_stage?.new_value || 'Undefined'}
          </span>
        </>,
      ];
    case PurchaseOrderActivityType.PURCHASE_ORDER_CREATED:
      return [
        <>
          created PO{' '}
          <span className="text-base-primary font-semibold">
            {purchaseOrder?.po_number || 'N/A'}
          </span>
        </>,
      ];
    case PurchaseOrderActivityType.DELIVERY_SCHEDULED:
      return [
        <>
          scheduled delivery for{' '}
          <span className="text-base-primary font-semibold">
            {activity.details?.delivery_date
              ? formatDate(
                  activity.details?.delivery_date,
                  'delivery-long',
                  'UTC',
                )
              : 'N/A'}
          </span>
        </>,
      ];
    case PurchaseOrderActivityType.DELIVERY_UPDATED:
      return [
        <>
          updated delivery for{' '}
          <span className="text-base-primary font-semibold">
            {activity.details?.delivery_date?.new_value
              ? formatDate(
                  activity.details?.delivery_date?.new_value,
                  'delivery-long',
                  'UTC',
                )
              : 'N/A'}
          </span>
        </>,
      ];
    case PurchaseOrderActivityType.PROP_65_SET:
      return [<>updated CA Prop 65</>];
    case PurchaseOrderActivityType.REMOVED_FROM_DELIVERY:
      return [<>removed this PO from the delivery</>];
    case PurchaseOrderActivityType.DELIVERY_SHIPPED:
      return [<>marked the delivery as shipped</>];
    case PurchaseOrderActivityType.DELIVERY_CANCELLED:
      return [<>cancelled the delivery</>];
    case PurchaseOrderActivityType.PURCHASE_ORDER_UPDATED:
      return [<>updated purchase order</>];
    case PurchaseOrderActivityType.CATALOG_ITEM_CREATED:
      return [<>created a catalog item</>];
    case PurchaseOrderActivityType.CATALOG_ITEM_UPDATED:
      return [<>updated a catalog item</>];
    case PurchaseOrderActivityType.PURCHASE_ORDER_LINE_CREATED:
      return [<>created a purchase order line</>];
    case PurchaseOrderActivityType.PURCHASE_ORDER_LINE_UPDATED:
      return [<>updated a purchase order line</>];
    case PurchaseOrderActivityType.PURCHASE_ORDER_LINES_UPDATED:
      return [<>updated purchase order lines</>];
    case PurchaseOrderActivityType.VENDOR_UPLOADED_INVOICE:
      return [<>uploaded invoice</>];
    case PurchaseOrderActivityType.VENDOR_UPLOADED_PACKING_LIST:
      return [<>uploaded packing list</>];
    case PurchaseOrderActivityType.REVISION_REQUESTED:
      return [
        <>sent a change request.</>,
        <PurchaseOrderRevisionRequestedActivity
          key={`${activity.id}_details`}
          detail={
            activity.details as PurchaseOrderRevisionRequestedActivityDetail
          }
        />,
      ];
    case PurchaseOrderActivityType.REVISION_STATUS_UPDATED:
      return [
        <>
          marked the revision as{' '}
          <span className="text-base-primary font-semibold">
            {(activity.details?.revision_status ===
              PurchaseOrderRevisionStatus.REVISION_APPROVED ||
              activity.details?.revision_status ===
                PurchaseOrderRevisionStatus.REVISION_READY_FOR_NS_UPDATE) &&
            activity.details?.with_changes
              ? 'Approved with Changes'
              : PurchaseOrderRevisionStatusMap[
                  activity.details
                    ?.revision_status as PurchaseOrderRevisionStatus
                ] || activity.details?.revision_status}
            .
          </span>
        </>,
        activity.details?.comment && (
          <P size="xsm" color="disabled">
            {activity.details.comment}
          </P>
        ),
      ];
    case PurchaseOrderActivityType.REVISION_UPDATED:
      return [
        <>updated the PO.</>,
        <PurchaseOrderRevisionRequestedActivity
          key={`${activity.id}_details`}
          detail={
            activity.details as PurchaseOrderRevisionRequestedActivityDetail
          }
        />,
      ];
    case PurchaseOrderActivityType.CANCELLED_REOPENED:
      return [
        <>
          reopened{' '}
          {purchaseOrder?.po_number ? (
            <span className="nu-text-base-primary font-semibold">
              {purchaseOrder?.po_number}
            </span>
          ) : null}
        </>,
      ];
    default:
      return [<>Not implemented</>];
  }
};

const getDisplayName = (author: PurchaseOrderActivityAuthor) => {
  const isInternal = author.type === PurchaseOrderActivityAuthorType.INTERNAL;
  if (isInternal) {
    return author.full_name ? author.full_name : 'NOVA';
  }
  return author.full_name || 'VP User';
};

const getBadge = (author: PurchaseOrderActivityAuthor) => {
  const isInternal = author.type === PurchaseOrderActivityAuthorType.INTERNAL;
  if (isInternal || !author.role) return undefined;
  return author.role === 'VENDOR' ? 'vendor' : 'fn_internal';
};

export const renderActivity = (
  activity: PurchaseOrderActivity,
  purchaseOrder: PurchaseOrder,
  isLast: boolean,
): React.ReactNode => {
  const { author } = activity;
  const isInternal = author.type === PurchaseOrderActivityAuthorType.INTERNAL;
  const name = getDisplayName(author);
  const badge = getBadge(author);
  const icon = isInternal ? 'ai/ai-02' : undefined;
  const [mainRender, secondaryRender] = renderActivityDetail(
    activity,
    purchaseOrder,
  );
  if (!mainRender) return null;
  return (
    <Activity
      key={activity.id}
      name={name}
      badgeType={badge}
      icon={icon}
      hideBar={isLast}
    >
      {secondaryRender ? (
        <Activity.EventDetailed
          name={name}
          date={activity.created_at}
          text={mainRender}
        >
          {secondaryRender}
        </Activity.EventDetailed>
      ) : (
        <Activity.Event name={name} date={activity.created_at}>
          {mainRender}
        </Activity.Event>
      )}
    </Activity>
  );
};
